import { useEffect, useRef } from 'react';
import { matchPath } from 'react-router-dom';
import { useProfile } from 'src/models';
import { useMediaQuery } from 'src/lib/hooks';
import { appRoutes } from 'src/routes/routes.types';
import { isReactNativeWebView } from 'src/utils/webview-detection';

// The avatar-menu pages an expired client can still open (#2779). Every other
// product URL shows the Expired window instead.
const EXPIRED_WINDOW_ALLOWED_PATHS: string[] = [
  appRoutes.profile,
  appRoutes.notificationSettings,
  appRoutes.company,
  appRoutes.companyPersonnel,
];

// Matched the way the router matches them (case-insensitive, trailing slash)
const isExpiredWindowAllowedPath = (pathname: string) =>
  EXPIRED_WINDOW_ALLOWED_PATHS.some((path) => matchPath({ path, end: true }, pathname));

/**
 * Whether the app is in its expired shell: the company has lost access and the
 * viewer is the client, not an impersonating admin. The mobile app's WebView is
 * out of scope and keeps its old behaviour.
 */
const useIsExpiredShell = () => {
  const { isExpired, isImpersonated } = useProfile({ enabled: false });

  return isExpired && !isImpersonated && !isReactNativeWebView();
};

/**
 * The expired shell on a phone (#2782): no sidebar, the logo in the header. One answer for the
 * layout and the header alike, so there's never a menu button without a menu, or the reverse.
 * Below Tailwind's md, the same line useBreakpoint().isMobile draws, with one media query.
 */
const useIsExpiredMobileShell = () => {
  const isExpiredShell = useIsExpiredShell();
  const isPhoneWidth = useMediaQuery('(max-width: 767px)');

  return isExpiredShell && isPhoneWidth;
};

// Focus and visibilitychange both fire on a tab switch; check once.
const RECHECK_THROTTLE_MS = 2 * 1000;

/**
 * While in the expired shell, re-check the company's state whenever the tab
 * regains focus, so a client who paid in another tab gets the product back
 * without a reload, on the window and on the avatar-menu pages alike. The
 * global refetchOnWindowFocus stays off; this only runs for expired clients.
 */
const useRecheckExpiredState = (enabled: boolean) => {
  const { query } = useProfile({ enabled: false });
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let lastCheck = 0;
    const recheck = () => {
      if (Date.now() - lastCheck < RECHECK_THROTTLE_MS) {
        return;
      }
      lastCheck = Date.now();
      // Join a refetch already in flight instead of cancelling it
      refetchRef.current({ cancelRefetch: false });
    };
    const recheckWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        recheck();
      }
    };

    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheckWhenVisible);

    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheckWhenVisible);
    };
  }, [enabled]);
};

export {
  useIsExpiredShell,
  useIsExpiredMobileShell,
  useRecheckExpiredState,
  isExpiredWindowAllowedPath,
};
